import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography } from '@mui/material'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import TwoWheelerOutlinedIcon from '@mui/icons-material/TwoWheelerOutlined'
import FormCardHeader from '../FormCardHeader'

/**
 * Presentational order detail shared by the restaurant and super-admin order
 * modals. `footer` holds any actions (accept / cancel) the caller wants below.
 */
export const BRAND = '#F47627'
export const BRAND_DARK = '#DC5F13'
const BRAND_SOFT = '#FFF3EB'
const INK = '#111827'
const MUTED = '#6B7280'
const LINE = '#EEF0F3'

const STATUS_TONES = {
  PENDING: { bg: '#FEF3C7', fg: '#92400E' },
  ACCEPTED: { bg: '#DBEAFE', fg: '#1E40AF' },
  ASSIGNED: { bg: '#E0E7FF', fg: '#3730A3' },
  PICKED: { bg: '#EDE9FE', fg: '#5B21B6' },
  DELIVERED: { bg: '#DCFCE7', fg: '#166534' },
  COMPLETED: { bg: '#DCFCE7', fg: '#166534' },
  CANCELLED: { bg: '#FEE2E2', fg: '#991B1B' }
}

const cardSx = {
  bgcolor: '#FFFFFF',
  border: `1px solid ${LINE}`,
  borderRadius: '14px',
  p: 2.5
}

const sectionTitleSx = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: MUTED,
  mb: 1.5
}

const fmtDate = v => {
  if (!v) return ''
  const d = new Date(Number.isNaN(Number(v)) ? v : Number(v))
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const Pill = ({ bg, fg, children }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      px: 1.25,
      py: 0.4,
      borderRadius: '999px',
      bgcolor: bg,
      color: fg,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: '0.03em',
      whiteSpace: 'nowrap'
    }}>
    {children}
  </Box>
)

const InfoRow = ({ icon: Icon, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 0.75 }}>
    <Icon sx={{ fontSize: 18, color: BRAND, mt: '2px', flexShrink: 0 }} />
    <Box
      sx={{ fontSize: 14, color: INK, minWidth: 0, wordBreak: 'break-word' }}>
      {children}
    </Box>
  </Box>
)

const MoneyRow = ({ label, value, strong }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      py: strong ? 0 : 0.75
    }}>
    <Typography
      sx={{
        fontSize: strong ? 16 : 14,
        fontWeight: strong ? 700 : 400,
        color: strong ? INK : MUTED
      }}>
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: strong ? 20 : 14,
        fontWeight: strong ? 800 : 500,
        color: INK,
        fontVariantNumeric: 'tabular-nums'
      }}>
      {value}
    </Typography>
  </Box>
)

function OrderDetailView({ order, currency = '', onClose, footer }) {
  const { t } = useTranslation()
  const money = v => `${currency} ${Number(v || 0).toFixed(2)}`
  const tone = STATUS_TONES[order.orderStatus] || {
    bg: '#F3F4F6',
    fg: '#374151'
  }
  const subtotal =
    Number(order.orderAmount || 0) -
    Number(order.deliveryCharges || 0) -
    Number(order.tipping || 0) -
    Number(order.taxationAmount || 0)
  const items = order.items || []

  return (
    <Box
      sx={{
        bgcolor: '#F7F8FA',
        borderRadius: '18px',
        overflow: 'hidden',
        border: `1px solid ${LINE}`
      }}>
      <FormCardHeader
        title={`${t('Order')} #${order.orderId}`}
        subtitle={fmtDate(order.createdAt)}
        onClose={onClose}
        action={
          order.orderStatus ? <Pill {...tone}>{order.orderStatus}</Pill> : null
        }
        sx={{
          px: 3,
          py: 2,
          bgcolor: '#FFFFFF',
          backgroundImage: `linear-gradient(180deg, ${BRAND_SOFT} 0%, #FFFFFF 100%)`,
          borderBottom: `1px solid ${LINE}`
        }}
      />

      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            md: 'minmax(0, 1.6fr) minmax(0, 1fr)'
          },
          alignItems: 'start'
        }}>
        {/* Left: what was ordered and where it goes */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={cardSx}>
            <Typography sx={sectionTitleSx}>{t('Items')}</Typography>
            {items.map((item, index) => {
              const options = (item.addons || []).flatMap(a => a.options || [])
              const unit =
                Number(item.variation?.price || 0) +
                options.reduce((s, o) => s + (o.price || 0), 0)
              return (
                <Box
                  key={item._id || index}
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    py: 1.5,
                    borderTop: index ? `1px solid ${LINE}` : 'none'
                  }}>
                  <Box
                    sx={{
                      minWidth: 34,
                      height: 28,
                      px: 0.75,
                      borderRadius: '8px',
                      bgcolor: BRAND_SOFT,
                      color: BRAND_DARK,
                      fontSize: 13,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                    {item.quantity}×
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{ fontSize: 15, fontWeight: 600, color: INK }}>
                      {item.title}
                      {item.variation?.title ? (
                        <Box
                          component="span"
                          sx={{ color: MUTED, fontWeight: 400 }}>
                          {` · ${item.variation?.title}`}
                        </Box>
                      ) : null}
                    </Typography>
                    {options.length > 0 && (
                      <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.25 }}>
                        {options.map(o => o.title).join(', ')}
                      </Typography>
                    )}
                    {!!item.specialInstructions && (
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: '#92400E',
                          bgcolor: '#FFFBEB',
                          border: '1px solid #FDE68A',
                          borderRadius: '8px',
                          px: 1,
                          py: 0.5,
                          mt: 0.75
                        }}>
                        {t('SpecialInstructions')}: {item.specialInstructions}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: INK,
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums'
                    }}>
                    {money(unit * item.quantity)}
                  </Typography>
                </Box>
              )
            })}
          </Box>

          <Box sx={cardSx}>
            <Typography sx={sectionTitleSx}>
              {order.isPickedUp ? t('Pickup') : t('DeliveryAddress')}
            </Typography>
            {order.isPickedUp ? (
              <InfoRow icon={StorefrontOutlinedIcon}>
                {order.restaurant?.address || order.restaurant?.name || '—'}
              </InfoRow>
            ) : (
              <InfoRow icon={PlaceOutlinedIcon}>
                {order.deliveryAddress?.label && (
                  <Box sx={{ fontWeight: 600 }}>
                    {order.deliveryAddress.label}
                  </Box>
                )}
                {order.deliveryAddress?.deliveryAddress || '—'}
                {order.deliveryAddress?.details && (
                  <Box sx={{ color: MUTED, fontSize: 13, mt: 0.25 }}>
                    {order.deliveryAddress.details}
                  </Box>
                )}
              </InfoRow>
            )}
            {order.rider?.name && (
              <InfoRow icon={TwoWheelerOutlinedIcon}>
                {order.rider.name}
              </InfoRow>
            )}
          </Box>
        </Box>

        {/* Right: who ordered and what they paid */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {order.user && (
            <Box sx={cardSx}>
              <Typography sx={sectionTitleSx}>{t('Customer')}</Typography>
              <InfoRow icon={PersonOutlineIcon}>
                <Box sx={{ fontWeight: 600 }}>{order.user.name || '—'}</Box>
              </InfoRow>
              {order.user.phone && (
                <InfoRow icon={PhoneOutlinedIcon}>
                  <Box
                    component="a"
                    href={`tel:${order.user.phone}`}
                    sx={{
                      color: INK,
                      textDecoration: 'none',
                      '&:hover': { color: BRAND }
                    }}>
                    {order.user.phone}
                  </Box>
                </InfoRow>
              )}
              {order.user.email && (
                <InfoRow icon={MailOutlineIcon}>{order.user.email}</InfoRow>
              )}
            </Box>
          )}

          <Box sx={cardSx}>
            <Typography sx={sectionTitleSx}>{t('Charges')}</Typography>
            <MoneyRow label={t('Subtotal')} value={money(subtotal)} />
            <MoneyRow
              label={t('DeliveryFee')}
              value={money(order.deliveryCharges)}
            />
            <MoneyRow
              label={t('TaxCharges')}
              value={money(order.taxationAmount)}
            />
            <MoneyRow label={t('Tip')} value={money(order.tipping)} />
            <Box sx={{ borderTop: `1px dashed ${LINE}`, my: 1.25 }} />
            <MoneyRow
              strong
              label={t('Total')}
              value={money(order.orderAmount)}
            />
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: '10px',
                bgcolor: BRAND_SOFT,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1
              }}>
              <Box>
                <Typography sx={{ fontSize: 12, color: MUTED }}>
                  {t('PaymentMethod')}
                </Typography>
                <Typography
                  sx={{ fontSize: 14, fontWeight: 700, color: BRAND_DARK }}>
                  {order.paymentMethod}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontSize: 12, color: MUTED }}>
                  {t('PaidAmount')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: INK,
                    fontVariantNumeric: 'tabular-nums'
                  }}>
                  {money(order.paidAmount)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {footer}
    </Box>
  )
}

export default OrderDetailView
